import { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import {
  ArrowLeft,
  Send,
  Loader2,
  Camera,
  X,
  CheckCircle,
  AlertCircle,
  Trash2,
  Home
} from 'lucide-react';

const MobileFormSubmission = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { api, operator } = useMobileAuth();
  
  const templateId = searchParams.get('templateId');
  const jobId = searchParams.get('jobId');
  
  const [template, setTemplate] = useState(null);
  const [job, setJob] = useState(null);
  const [responses, setResponses] = useState({});
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  
  const signatureRef = useRef(null);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchData = async () => {
      if (!templateId) {
        toast.error('No form template specified');
        navigate('/mobile/forms');
        return;
      }

      try {
        setLoading(true);
        
        // Fetch template
        const templateRes = await api.get(`/api/form-templates/${templateId}`);
        setTemplate(templateRes.data);
        
        // Initialize responses with default values
        const initialResponses = {};
        templateRes.data.fields?.forEach(field => {
          const fieldType = field.field_type || field.type;
          if (fieldType === 'checkbox') {
            initialResponses[field.id] = false;
          } else if (fieldType === 'select' && field.options?.length > 0) {
            initialResponses[field.id] = '';
          } else {
            initialResponses[field.id] = '';
          }
        });
        setResponses(initialResponses);
        
        // Fetch job if job-linked
        if (jobId) {
          const jobRes = await api.get(`/api/jobs/${jobId}`);
          setJob(jobRes.data);
        }
      } catch (error) {
        console.error('Failed to fetch form data:', error);
        toast.error('Failed to load form');
        navigate('/mobile/forms');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [api, templateId, jobId, navigate]);

  const handleResponseChange = (fieldId, value) => {
    setResponses(prev => ({ ...prev, [fieldId]: value }));
    // Clear error when field is modified
    if (errors[fieldId]) {
      setErrors(prev => ({ ...prev, [fieldId]: null }));
    }
  };

  const handlePhotoCapture = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/api/form-submissions/upload-photo', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setPhotos(prev => [...prev, res.data.url]);
      toast.success('Photo added');
    } catch (error) {
      toast.error('Failed to upload photo');
    }
  };

  const removePhoto = (index) => {
    setPhotos(prev => prev.filter((_, i) => i !== index));
  };

  const clearSignature = () => {
    signatureRef.current?.clear();
  };

  const validateForm = () => {
    const newErrors = {};
    
    template?.fields?.forEach(field => {
      if (field.required) {
        const value = responses[field.id];
        if (value === '' || value === null || value === undefined) {
          newErrors[field.id] = 'This field is required';
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setSubmitting(true);
    try {
      // Get signature data
      let signatureData = null;
      if (signatureRef.current && !signatureRef.current.isEmpty()) {
        signatureData = signatureRef.current.toDataURL();
      }

      const submissionData = {
        template_id: templateId,
        job_id: jobId || null,
        responses,
        signature_data: signatureData,
        photos
      };

      await api.post('/api/form-submissions', submissionData);
      
      toast.success('Form submitted successfully!');
      navigate('/mobile/forms?tab=submitted');
    } catch (error) {
      console.error('Failed to submit form:', error);
      toast.error('Failed to submit form');
    } finally {
      setSubmitting(false);
    }
  };

  const renderField = (field) => {
    const hasError = errors[field.id];
    const fieldType = field.field_type || field.type;
    
    switch (fieldType) {
      case 'text':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-white">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              className={`bg-[#1a1a1a] border-gray-600 text-white ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'textarea':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-white">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Textarea
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              rows={3}
              className={`bg-[#1a1a1a] border-gray-600 text-white ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'number':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-white">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="number"
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              placeholder={field.placeholder || ''}
              className={`bg-[#1a1a1a] border-gray-600 text-white ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'select':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-white">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Select
              value={responses[field.id] || ''}
              onValueChange={(value) => handleResponseChange(field.id, value)}
            >
              <SelectTrigger className={`bg-[#1a1a1a] border-gray-600 text-white ${hasError ? 'border-red-500' : ''}`}>
                <SelectValue placeholder="Select an option" />
              </SelectTrigger>
              <SelectContent>
                {field.options?.map((option, idx) => (
                  <SelectItem key={idx} value={option}>
                    {option}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'checkbox':
        return (
          <div key={field.id} className="flex items-center space-x-2">
            <Checkbox
              id={field.id}
              checked={responses[field.id] || false}
              onCheckedChange={(checked) => handleResponseChange(field.id, checked)}
            />
            <Label htmlFor={field.id} className="text-white cursor-pointer">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
          </div>
        );

      case 'date':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-white">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="date"
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              className={`bg-[#1a1a1a] border-gray-600 text-white ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      case 'time':
        return (
          <div key={field.id} className="space-y-2">
            <Label className="text-white">
              {field.label}
              {field.required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input
              type="time"
              value={responses[field.id] || ''}
              onChange={(e) => handleResponseChange(field.id, e.target.value)}
              className={`bg-[#1a1a1a] border-gray-600 text-white ${hasError ? 'border-red-500' : ''}`}
            />
            {hasError && <p className="text-red-500 text-xs">{hasError}</p>}
          </div>
        );

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
      </div>
    );
  }

  if (!template) {
    return (
      <div className="min-h-screen bg-[#1a1a1a] flex items-center justify-center">
        <div className="text-center text-gray-500">
          <AlertCircle className="h-12 w-12 mx-auto mb-3" />
          <p>Form not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#1a1a1a]">
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-3 flex items-center justify-between sticky top-0 z-10">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/mobile/forms')} className="text-gray-400 p-1">
            <ArrowLeft className="h-6 w-6" />
          </button>
          <div className="flex-1">
            <h1 className="text-white font-semibold">{template.name}</h1>
            {job && (
              <p className="text-xs text-gray-400">
                {job.customer_name} - {job.site_name || 'Job'}
              </p>
            )}
          </div>
        </div>
        <button 
          onClick={() => navigate('/mobile/dashboard')} 
          className="text-gray-400 p-1"
          data-testid="home-btn"
        >
          <Home className="h-5 w-5" />
        </button>
      </div>

      {/* Form Content */}
      <div className="p-4 space-y-6 pb-24">
        {/* Form Description */}
        {template.description && (
          <p className="text-gray-400 text-sm">{template.description}</p>
        )}

        {/* Form Fields */}
        <div className="space-y-4">
          {template.fields?.map(field => renderField(field))}
        </div>

        {/* Photo Upload */}
        <div className="space-y-2">
          <Label className="text-white">Photos (Optional)</Label>
          <div className="flex flex-wrap gap-2">
            {photos.map((photo, index) => (
              <div key={index} className="relative w-20 h-20">
                <img
                  src={photo}
                  alt={`Photo ${index + 1}`}
                  className="w-full h-full object-cover rounded-lg"
                />
                <button
                  onClick={() => removePhoto(index)}
                  className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
                >
                  <X className="h-3 w-3 text-white" />
                </button>
              </div>
            ))}
            <button
              onClick={() => fileInputRef.current?.click()}
              className="w-20 h-20 border-2 border-dashed border-gray-600 rounded-lg flex items-center justify-center hover:border-[#ffd108] transition-colors"
            >
              <Camera className="h-6 w-6 text-gray-400" />
            </button>
          </div>
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={handlePhotoCapture}
            className="hidden"
          />
        </div>

        {/* Signature */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-white">Signature (Optional)</Label>
            <button
              onClick={clearSignature}
              className="text-xs text-gray-400 hover:text-white flex items-center gap-1"
            >
              <Trash2 className="h-3 w-3" />
              Clear
            </button>
          </div>
          <div className="bg-white rounded-lg overflow-hidden">
            <SignatureCanvas
              ref={signatureRef}
              canvasProps={{
                className: 'w-full h-32'
              }}
              backgroundColor="white"
            />
          </div>
          <p className="text-xs text-gray-500">Sign above using your finger</p>
        </div>
      </div>

      {/* Submit Button - Fixed at bottom */}
      <div className="fixed bottom-0 left-0 right-0 p-4 bg-[#2a2a2a] border-t border-gray-700">
        <Button
          onClick={handleSubmit}
          disabled={submitting}
          className="w-full bg-[#ffd108] hover:bg-[#e5bc07] text-black font-semibold"
        >
          {submitting ? (
            <Loader2 className="h-4 w-4 animate-spin mr-2" />
          ) : (
            <Send className="h-4 w-4 mr-2" />
          )}
          {submitting ? 'Submitting...' : 'Submit Form'}
        </Button>
      </div>
    </div>
  );
};

export default MobileFormSubmission;
